<template>
	<v-row :key="key" no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmVaccines" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('ccm-vaccines-header') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloading" />

							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								@click="synchronizeAddDrawers('ccmVaccineAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="vaccines"
						:headers="headers"
						:items-per-page="-1"
						style="overflow-y: auto"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						dense
						sort-by="vaccinationDate"
						sort-desc
						class="data-table-outline"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, index }">
							<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item)">
								<td class="text-left px-1">
									{{ item.vaccineName }}
								</td>
								<td class="text-left px-1">
									{{ item.lotNumber }}
								</td>
								<td class="text-left px-1 truncate-text" style="max-width: 20vw">
									{{ item.notes }}
								</td>
								<td class="text-left px-1">
									{{ item.performedBy }}
								</td>
								<td class="text-left px-1">
									{{ formatTimestamp(item.vaccinationDate) }}
								</td>
								<td class="text-left px-1">
									{{ formatDuration(item.timeSpentSeconds) }}
								</td>
								<td class="px-1 text-right">
									<span v-if="item.ccmRecordId === selectedCCMRecord.id">
										<ActionButton
											icon="mdi-delete"
											tooltip="delete"
											:maxSize="25"
											:minSize="20"
											@clicked="
												deleteId = item.id;
												showDeleteDialog = true;
											"
											:busy="deletingIds.includes(item.id)" />
										<v-tooltip left>
											<template v-slot:activator="{ on }">
												<v-icon
													v-on="on"
													:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
													color="primary"
													@click.stop="openEditDrawer(item)">
													> mdi-pencil
												</v-icon>
											</template>
											{{ $t('edit') }}
										</v-tooltip>
									</span>
									<!-- <span v-else> -->
									<!-- 	<v-tooltip top> -->
									<!-- 		<template v-slot:activator="{ on }"> -->
									<!-- 			<v-icon -->
									<!-- 				v-on="on" -->
									<!-- 				:size="$vuetify.breakpoint.mdAndDown ? 20 : 25" -->
									<!-- 				color="grey" -->
									<!-- 			> -->
									<!-- 				mdi-alert-circle-outline -->
									<!-- 			</v-icon> -->
									<!-- 		</template> -->
									<!-- 		{{ $t("not-actions-available") }} -->
									<!-- 	</v-tooltip> -->
									<!-- </span> -->
								</td>
							</tr>
						</template>
						<template v-slot:[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>
		<CCMVaccineViewDialog
			:showDialog.sync="showViewDialog"
			v-if="showViewDialog && selectedVaccine"
			:selectedVaccine="selectedVaccine"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deleteCCMVaccine(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatTimestamp,
	formatDuration,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import CCMVaccineViewDialog from '../../../dialogs/CCMVaccineViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMVaccines',
	components: { CCMVaccineViewDialog, DeleteDialog, UserTimeTracker, ActionButton },

	data () {
		return {
			key: 0, // Used to force re-rendering of the component
			dataLoading: true,
			deleteId: -1,
			deletingIds: [],
			selectedVaccine: null,
			showDeleteDialog: false,
			showViewDialog: false,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			vaccines: (state) => state.ccmVaccines.ccmVaccinesByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{
					text: this.$t('name'),
					sortable: false,
					value: 'vaccineName',
					class: 'px-1',
				},
				{
					text: this.$t('lot-number-header'),
					sortable: false,
					value: 'lotNumber',
					class: 'px-1',
				},
				{
					text: this.$t('notes'),
					sortable: false,
					value: '',
					class: 'px-1',
				},
				{
					text: this.$t('performed-by'),
					sortable: false,
					value: 'performedBy',
					class: 'px-1',
				},
				{
					text: this.$t('date'),
					sortable: false,
					value: 'vaccinationDate',
					class: 'px-1',
				},
				{
					text: this.$t('time-spent-header'),
					sortable: false,
					value: 'timeSpentSeconds',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getCCMVaccines: 'ccmVaccines/getCCMVaccinesByPatientId',
			deleteCCMVaccine: 'ccmVaccines/deleteCCMVaccine',
		}),
		...mapMutations({
			storeCCMVaccine: 'ccmVaccines/STORE_CCM_VACCINE',
		}),

		getRowsForDataTable,
		formatTimestamp,
		formatDuration,
		downloadCSVWithKeys,
		getFullName,

		async deleteVaccineEvent (id) {
			await this.deleteVaccine(id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},

		async openEditDrawer (vaccine) {
			await this.storeCCMVaccine(vaccine);
			this.synchronizeEditDrawers('ccmVaccineEdit');
		},
		openViewDialog (vaccine) {
			this.selectedVaccine = vaccine;
			this.showViewDialog = true;
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
		downloadCSV () {
			this.downloading = true;
			this.downloadCSVWithKeys(this.vaccines, 'Vaccines_for_' + this.getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: [
					'vaccineName',
					'dose',
					'route',
					'site',
					'manufacturer',
					'lotNumber',
					'notes',
					'performedBy',
					'performedByRole',
				],
				timestamps: ['vaccinationDate'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
	},

	watch: {
		selectedCCMRecord () {
			this.key = !this.key;
		},
	},

	async mounted () {
		await this.getCCMVaccines(this.detailedUser.userData.id);
		this.dataLoading = false;
	},
};
</script>
